import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { BATTERY_OFFER_TYPE, MODAL_NAMES } from '../../../Product-JP.constants'

import translations from './createCustomizationFunnelData.translations'
import type { Step } from './types'
import { mapStep } from './utils'

const STEP_IMAGES: Record<string, string> = {
  grades: '/img/product/jp/step-grades.png',
  battery: '/img/product/jp/step-battery.png',
  dual_sim: '/img/product/jp/step-sim.png',
  processor_type_and_speed: '/img/product/jp/step-processor.png',
  processor_type_and_graphic_card: '/img/product/jp/step-processor.png',
  processor: '/img/product/jp/step-processor.png',
}

const STEP_LABEL: Record<string, I18nDefinition> = {
  grades: translations.gradesLabel,
  battery: translations.batteryLabel,
  dual_sim: translations.simLabel,
  processor_type_and_speed: translations.processorLabel,
  processor_type_and_graphic_card: translations.processorLabel,
  processor: translations.processorLabel,
}

const STEP_DESCRIPTION: Record<string, I18nDefinition> = {
  grades: translations.gradesDescrition,
  battery: translations.batteryDescrition,
  dual_sim: translations.simDescription,
  processor_type_and_speed: translations.processorDescription,
  processor_type_and_graphic_card: translations.processorDescription,
  processor: translations.processorDescription,
}

const STEP_MODALS: Record<string, string> = {
  grades: MODAL_NAMES.CONDITION,
  battery: MODAL_NAMES.ABOUT_BATTERY,
}

const STEP_MODALS_UPDATED: Record<string, string> = {
  ...STEP_MODALS,
  battery: MODAL_NAMES.ABOUT_BATTERY_UPDATED,
}

const STEP_DESCRIPTION_UPDATED: Record<string, I18nDefinition> = {
  ...STEP_DESCRIPTION,
  battery: translations.batteryDescriptionUpdated,
}

// Battery labels are usually provided by backend, but for AB testing purpose
// we need to hardcode these labels temporarily (as we're displaying another version of the battery picker).
const BATTERY_OPTION_LABELS: Record<number, I18nDefinition> = {
  [BATTERY_OFFER_TYPE.STANDARD]: translations.batteryUpdatedStandard,
  [BATTERY_OFFER_TYPE.NINETY_NINE]: translations.batteryUpdated9099,
  [BATTERY_OFFER_TYPE.NEW]: translations.batteryUpdatedNew,
}

// Battery sublabels are usually provided by backend, but for AB testing purpose
// we need to hardcode these labels temporarily (as we're displaying another version of the battery picker).
const BATTERY_OPTION_SUBLABELS: Record<number, I18nDefinition> = {
  [BATTERY_OFFER_TYPE.STANDARD]: translations.batteryUpdatedStandardSublabel,
  [BATTERY_OFFER_TYPE.NINETY_NINE]: translations.batteryUpdated9099Sublabel,
  [BATTERY_OFFER_TYPE.NEW]: translations.batteryUpdatedNewSublabel,
}

// When battery options are sold out, the backend returns specialOfferType: null.
// Since the backend order is guaranteed (standard -> 90-99% -> new battery),
// we can resolve the offer type by position.
const BATTERY_OFFER_TYPES_BY_COUNT: Record<number, number[]> = {
  2: [BATTERY_OFFER_TYPE.STANDARD, BATTERY_OFFER_TYPE.NEW],
  3: [
    BATTERY_OFFER_TYPE.STANDARD,
    BATTERY_OFFER_TYPE.NINETY_NINE,
    BATTERY_OFFER_TYPE.NEW,
  ],
}

function resolveBatteryOfferType(
  option: {
    parameters?: { specialOfferType?: { value: number } | null } | null
  },
  index: number,
  totalOptions: number,
): number | undefined {
  return (
    option.parameters?.specialOfferType?.value ??
    BATTERY_OFFER_TYPES_BY_COUNT[totalOptions]?.[index]
  )
}

export type CreateCustomizationFunnelDataOptions = {
  useBatteryPickerUpdated?: boolean
}

/**
 * This function is here to map the response of existing endpoints (pickers, best_offers) to
 * something easily usable by the Customization Funnel.
 *
 * We chose to reuse existing endpoints over a new dedicated one to gain some time developing the
 * MVP that will be A/B tested on smartphones only. This is not meant to be scaled to any other category
 * without a new endpoint to be created.
 */
export const createCustomizationFunnelData = (
  i18n: ReturnType<typeof useI18n>,
  pickers: GetPickersResponse['pickerGroups'],
  options: CreateCustomizationFunnelDataOptions = {},
  experiments: Record<`experiment.${string}`, string> = {},
): Step[] => {
  const { useBatteryPickerUpdated = false } = options
  const stepModals = useBatteryPickerUpdated ? STEP_MODALS_UPDATED : STEP_MODALS
  const stepDescriptions = useBatteryPickerUpdated
    ? STEP_DESCRIPTION_UPDATED
    : STEP_DESCRIPTION
  const steps: Array<Step | null> = pickers
    .map((group) => mapStep(group))
    .map((group) => {
      if (!useBatteryPickerUpdated && group.id === 'battery') {
        return {
          ...group,
          options: group.options.filter(
            // We want to AB test a version where:
            // we still have only two choices (standard, new battery) -> we need this filter to keep this as is (until the AB test is finished at least)
            // and the new version with three choices (standard, 90-99%, new battery)
            // We use resolveBatteryOfferType to handle sold-out options where specialOfferType is null
            (option, index) =>
              resolveBatteryOfferType(option, index, group.options.length) !==
              BATTERY_OFFER_TYPE.NINETY_NINE,
          ),
        }
      }

      return group
    })
    .map((group) => ({
      ...group,
      options: group.options.map((option, index) => {
        if (group.id === 'grades') {
          return {
            ...option,
            label: i18n(translations[`grade${option.trackingValue}`]),
          }
        }

        const specialOfferType =
          useBatteryPickerUpdated && group.id === 'battery'
            ? resolveBatteryOfferType(option, index, group.options.length)
            : option.parameters?.specialOfferType?.value

        if (
          useBatteryPickerUpdated &&
          group.id === 'battery' &&
          specialOfferType !== undefined
        ) {
          return {
            ...option,
            label:
              specialOfferType in BATTERY_OPTION_LABELS
                ? i18n(BATTERY_OPTION_LABELS[specialOfferType])
                : option.label,
            subLabel:
              specialOfferType in BATTERY_OPTION_SUBLABELS
                ? i18n(BATTERY_OPTION_SUBLABELS[specialOfferType])
                : option.subLabel,
          }
        }

        return option
      }),
    }))
    .map((group) => ({
      ...group,
      guidance: group.guidance
        ? {
            ...group.guidance,
            image: group.id in STEP_IMAGES ? STEP_IMAGES[group.id] : undefined,
            label:
              group.id in STEP_LABEL
                ? i18n(STEP_LABEL[group.id])
                : group.guidance.label,
            description:
              group.id in stepDescriptions
                ? i18n(stepDescriptions[group.id])
                : undefined,
            modalName:
              group.id in stepModals
                ? stepModals[group.id]
                : group.guidance?.modalName,
          }
        : undefined,
    }))
    .filter(Boolean)

  if (experiments['experiment.tradeInJP'] === 'enabled') {
    const tradeInJPStep: Step = {
      id: 'trade_in_jp',
      title: translations.tradeInStepTitle,
      titleEmphasis: null,
      options: [],
    }
    steps.push(tradeInJPStep)
  }

  return steps as unknown as Step[]
}
